import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trophy, Users, Calendar, Loader2, AlertCircle, ChevronLeft, ChevronRight, Clock, Award } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { LazyImage } from '../shared/LazyImage';

interface Contest {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'completed';
  vote_price: number;
  contestants_count?: number;
  votes_count?: number;
  slug: string;
  photo_url?: string;
  logo_url?: string;
  is_public: boolean;
  code_unique: string;
}

interface ContestListProps {
  filter?: string;
  search?: string;
}

// Types pour le retour de la fonction formatDate
interface FormattedDateFull {
  date: string;
  time: string;
}

// Fonction pour formater une date en français
const formatDate = (dateString: string, format: 'short' | 'full' = 'short'): string | FormattedDateFull => {
  const date = new Date(dateString);
  
  if (format === 'short') {
    const datePart = date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric'
    });
    const timePart = date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `${datePart} à ${timePart}`;
  } else {
    // Format complet
    const day = date.getDate();
    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    
    // Retourne un objet avec les différentes parties de la date
    return {
      date: `${day} ${month} ${year}`,
      time: `${hours} h : ${minutes < 10 ? '0' + minutes : minutes} min`
    };
  }
};

// Composant pour le chronomètre
const CountdownTimer = ({ targetDate, type }: { targetDate: string, type: 'start' | 'end' }) => {
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const totalSeconds = Math.max(0, differenceInSeconds(target, now));
      
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = Math.floor(totalSeconds % 60);
      
      setTimeLeft({ days, hours, minutes, seconds });
    };
    
    // Calculer immédiatement
    calculateTimeLeft();
    
    // Mettre à jour chaque seconde
    const timer = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  const isExpired = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;
  
  if (isExpired) {
    return (
      <div className="text-xs text-white">
        {type === 'start' ? 'Démarrage imminent' : 'Terminé'}
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center">
      <div className="text-yellow-400 text-xs font-medium mb-1">
        {type === 'start' ? 'Avant début' : 'Temps restant'}
      </div>
      <div className="flex items-center gap-1">
        {timeLeft.days > 0 && (
          <div className="flex flex-col items-center px-1">
            <span className="text-white font-bold text-xs">{timeLeft.days}</span>
            <span className="text-yellow-400 text-[10px]">jours</span>
          </div>
        )}
        <div className="flex flex-col items-center px-1">
          <span className="text-white font-bold text-xs">{String(timeLeft.hours).padStart(2, '0')}</span>
          <span className="text-yellow-400 text-[10px]">h</span>
        </div>
        <div className="flex flex-col items-center px-1">
          <span className="text-white font-bold text-xs">{String(timeLeft.minutes).padStart(2, '0')}</span>
          <span className="text-yellow-400 text-[10px]">min</span>
        </div>
        <div className="flex flex-col items-center px-1">
          <span className="text-white font-bold text-xs">{String(timeLeft.seconds).padStart(2, '0')}</span>
          <span className="text-yellow-400 text-[10px]">sec</span>
        </div>
      </div>
    </div>
  );
};

export const ContestList: React.FC<ContestListProps> = ({ filter = 'all', search = '' }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const queryClient = useQueryClient();

  const { data: contests, isLoading, error } = useQuery({
    queryKey: ['contests', filter, search],
    queryFn: async () => {
      // Utilisation de la fonction Supabase optimisée
      const { data, error } = await supabase.rpc('get_public_contests_with_counts');
      if (error) throw error;
      let filtered = data;
      // Filtrer côté front selon le filtre choisi
      if (filter !== 'all') {
        if (filter === 'active') filtered = filtered.filter((c: any) => c.status === 'active');
        else if (filter === 'upcoming') filtered = filtered.filter((c: any) => new Date(c.start_date) > new Date());
        else if (filter === 'ended') filtered = filtered.filter((c: any) => c.status === 'completed');
      }
      if (search) {
        const s = search.toLowerCase();
        filtered = filtered.filter((c: any) => c.name.toLowerCase().includes(s) || c.description.toLowerCase().includes(s));
      }
      return filtered;
    },
    refetchInterval: 60000,
  });

  // Tri des concours par statut : 1) En cours, 2) Terminés, 3) En attente
  const sortedContests = contests ? [...contests].sort((a, b) => {
    // Définir la priorité de chaque statut
    const statusPriority: Record<string, number> = {
      'active': 1,    // En cours - priorité la plus haute
      'completed': 2, // Terminés - priorité moyenne
      'draft': 3      // En attente - priorité la plus basse
    };
    
    // Trier selon la priorité du statut
    return statusPriority[a.status] - statusPriority[b.status];
  }) : [];

  // Pagination logic
  const totalPages = sortedContests ? Math.ceil(sortedContests.length / itemsPerPage) : 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentContests = sortedContests ? sortedContests.slice(startIndex, endIndex) : [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64 text-destructive">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>Une erreur est survenue lors du chargement des concours</span>
      </div>
    );
  }

  if (!contests?.length) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Aucun concours trouvé
        </h3>
        <p className="text-gray-500">
          {search
            ? "Aucun concours ne correspond à votre recherche"
            : filter !== 'all'
            ? "Aucun concours dans cette catégorie pour le moment"
            : "Aucun concours n'est disponible pour le moment"}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {currentContests.map((contest) => (
          <Link
            key={contest.id}
            to={`/c/${contest.slug}`}
            className="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:translate-y-[-4px]"
          >
            {/* Carte principale */}
            <div className="relative bg-gradient-to-b from-gray-900/95 to-black border border-white/10 rounded-2xl overflow-hidden">
              {/* Image de couverture avec overlay */}
              <div className="relative h-72 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent z-10"></div>
                <LazyImage
                  src={contest.logo_url || contest.photo_url || `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/candidates/${contest.id}`}
                  alt={contest.name}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 ease-out"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1470&auto=format&fit=crop';
                  }}
                />
              </div>

              {/* Contenu */}
              <div className="relative pt-10 px-6 pb-6">
                {/* Badge Award */}
                {contest.award && (
                  <div className="absolute top-[-12px] right-2 z-20">
                    <Link 
                      to={`/awards/${contest.award?.slug || ''}`} 
                      className="block"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = `/awards/${contest.award?.slug || ''}`;
                      }}
                    >
                      <div className="flex items-center gap-1 bg-yellow-400/90 px-2 py-1 rounded-full shadow-md hover:bg-yellow-500/90 transition-colors">
                        {contest.award.logo_url ? (
                          <img 
                            src={contest.award.logo_url} 
                            alt={contest.award.name} 
                            className="w-4 h-4 rounded-full object-cover"
                          />
                        ) : (
                          <Award className="w-3 h-3 text-black" />
                        )}
                        <span className="text-[10px] font-medium text-black truncate max-w-[80px] md:truncate-none md:max-w-none">{contest.award.name}</span>
                      </div>
                    </Link>
                  </div>
                )}
                {/* Badge de statut */}
                <div className={`absolute top-[-12px] ${contest.award ? 'left-2' : 'left-1/2 transform -translate-x-1/2'} z-20`}>
                  {contest.status === 'active' && (
                    <Badge 
                      className="px-4 py-1 text-xs font-medium rounded-full bg-emerald-500 border-none text-white"
                    >
                      En cours
                    </Badge>
                  )}
                  {contest.status === 'completed' && (
                    <Badge 
                      className="px-4 py-1 text-xs font-medium rounded-full bg-gray-500 border-none text-white"
                    >
                      Terminé
                    </Badge>
                  )}
                  {contest.status === 'draft' && (
                    <Badge 
                      className="px-4 py-1 text-xs font-medium rounded-full bg-orange-500 border-none text-white"
                    >
                      A venir
                    </Badge>
                  )}
                </div>

                {/* Titre et Description */}
                <div className="mb-6 text-center">
                  <h3 className="text-xl font-fancy font-bold mb-2 text-white group-hover:text-yellow-400 transition-colors duration-300">
                    {contest.name}
                  </h3>
                  <p className="text-gray-400 text-sm font-accent line-clamp-2">
                    {contest.description}
                  </p>
                </div>

                {/* Statistiques et Chronomètre sur une ligne */}
                <div className="grid grid-cols-5 gap-2 mb-6">
                  {/* Participants */}
                  <div className="flex flex-col items-center col-span-1">
                    <div className="bg-black/30 p-2 rounded-full border border-white/10 mb-2">
                      <Users className="w-5 h-5 text-yellow-400" />
                    </div>
                    <span className="text-white font-bold text-xs">{contest.contestants_count || 0}</span>
                    <span className="text-yellow-400 text-xs">Participants</span>
                  </div>
                  
                  {/* Date de fin/début */}
                  <div className="flex flex-col items-center col-span-2">
                    <div className="bg-black/30 p-2 rounded-full border border-white/10 mb-2">
                      <Calendar className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="text-white font-bold text-xs text-center">
                      {contest.status === 'draft' && (
                        <>
                          <div className="text-yellow-400 font-medium">Début</div>
                          <div className="text-white text-[10px]">
                            {(() => {
                              const formattedDate = formatDate(contest.start_date, 'full') as FormattedDateFull;
                              return (
                                <>
                                  <div>{formattedDate.date}</div>
                                  <div className="text-gray-300">{formattedDate.time}</div>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                      {contest.status !== 'draft' && (
                        <>
                          <div className="text-yellow-400 font-medium">Fin</div>
                          <div className="text-white text-[10px]">
                            {(() => {
                              const formattedDate = formatDate(contest.end_date, 'full') as FormattedDateFull;
                              return (
                                <>
                                  <div>{formattedDate.date}</div>
                                  <div className="text-gray-300">{formattedDate.time}</div>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Chronomètre */}
                  <div className="flex flex-col items-center col-span-2">
                    <div className="bg-black/30 p-2 rounded-full border border-white/10 mb-2">
                      <Clock className="w-5 h-5 text-yellow-400" />
                    </div>
                    {contest.status === 'draft' ? (
                      <CountdownTimer targetDate={contest.start_date} type="start" />
                    ) : contest.status === 'active' ? (
                      <CountdownTimer targetDate={contest.end_date} type="end" />
                    ) : (
                      <div className="text-xs text-white">
                        <span className="text-white font-bold">Terminé</span>
                        <div className="text-yellow-400 text-xs">-</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bouton */}
                <div>
                  {contest.status === 'draft' ? (
                    <Link to={`/inscription/${contest.code_unique}`}>
                      <button className="w-full py-3 bg-yellow-400 hover:bg-yellow-500 rounded-lg text-black font-bold transition-colors duration-200">
                        S'inscrire
                      </button>
                    </Link>
                  ) : (
                    <button className="w-full py-3 bg-yellow-400 hover:bg-yellow-500 rounded-lg text-black font-bold transition-colors duration-200">
                      Découvrir
                    </button>
                  )}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Pagination */}
      {contests && contests.length > 0 && (
        <div className="mt-12 flex justify-center items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (currentPage > 1) handlePageChange(currentPage - 1);
            }}
            disabled={currentPage === 1}
            className="border-yellow-400/20 hover:bg-yellow-400/10 hover:text-yellow-400 rounded-full w-10 h-10 p-0 z-10"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              variant={currentPage === page ? "default" : "outline"}
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePageChange(page);
              }}
              className={cn(
                "rounded-full w-10 h-10 p-0 font-bold z-10",
                currentPage === page 
                  ? "bg-yellow-400 hover:bg-yellow-500 text-black" 
                  : "border-yellow-400/20 hover:bg-yellow-400/10 hover:text-yellow-400"
              )}
            >
              {page}
            </Button>
          ))}
          
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (currentPage < Math.max(totalPages, 1)) handlePageChange(currentPage + 1);
            }}
            disabled={currentPage === Math.max(totalPages, 1)}
            className="border-yellow-400/20 hover:bg-yellow-400/10 hover:text-yellow-400 rounded-full w-10 h-10 p-0 z-10"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
